/**
 * Role hierarchy for Optix dashboard widget visibility.
 * Higher index = higher privilege.
 *
 * OWNER > MANAGER > CLIENT_SERVICES > RECEPTIONIST
 */

export const ROLE_HIERARCHY = ['RECEPTIONIST', 'CLIENT_SERVICES', 'MANAGER', 'OWNER']

/** Role level (0 = lowest, 3 = highest) */
const ROLE_LEVEL = Object.fromEntries(ROLE_HIERARCHY.map((r, i) => [r, i]))

/**
 * Check if userRole meets or exceeds the minimum role required for a widget.
 * @param {string} userRole - Current user's role (e.g. 'MANAGER')
 * @param {string} minRole - Minimum role required (e.g. 'MANAGER' = manager and above)
 * @returns {boolean} True if user can view the widget
 */
export function canViewWidget(userRole, minRole) {
  if (!minRole) return true
  if (!userRole) return false
  const userLevel = ROLE_LEVEL[userRole.toUpperCase()]
  const minLevel = ROLE_LEVEL[minRole.toUpperCase()]
  if (userLevel === undefined || minLevel === undefined) return false
  return userLevel >= minLevel
}

/**
 * Normalize role string (handles Optix API variations).
 * @param {string} role - Raw role from API or URL
 * @returns {string|null} Normalized role or null
 */
export function normalizeRole(role) {
  if (!role || typeof role !== 'string') return null
  const upper = role.toUpperCase().replace(/\s+/g, '_')
  // Map common variations to our hierarchy
  const map = {
    OWNER: 'OWNER',
    MANAGER: 'MANAGER',
    ADMIN: 'MANAGER',
    CLIENT_SERVICES: 'CLIENT_SERVICES',
    CLIENT_REPRESENTATIVE: 'CLIENT_SERVICES',
    CLIENT_REP: 'CLIENT_SERVICES',
    RECEPTIONIST: 'RECEPTIONIST',
    RECEPTION: 'RECEPTIONIST'
  }
  return map[upper] ?? (ROLE_HIERARCHY.includes(upper) ? upper : null)
}
